import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useCompany } from '../../hooks/useCompany'
import { supabase } from '../../services/supabase'
import { SUPPLIER_RISK_ALERT_DOC_UPLOAD_CONFIG } from '../../utils/constants'
import { ImpactFeedbackPanel, SupplierRiskAlertDocument } from './SupplierRiskAlertDocument'
import {
  IMPACT_AREAS,
  IMPACT_DURATIONS,
  SUPPLY_STATUSES,
  alertHeadline,
  getCompanyLogoUrl,
  listSuppliersFromPayload,
  toggleImpactArea,
  type AlertPayload,
  type AlertSupplierRow,
  type ImpactFeedback,
} from './supplierRiskAlertShared'
import { EsBadge } from './ui'

const field =
  'w-full rounded-lg border border-es-hairline bg-es-white px-3 py-2 text-sm text-es-navy focus:outline-none focus:ring-2 focus:ring-es-signal/40'

const SIGNED_URL_EXPIRY_SEC = 60

const CSV_BASE_HEADERS = [
  'sent_at',
  'status',
  'title',
  'supplier',
  'category',
  'brand',
  'unique_id',
  'feedback_mode',
  'progress',
] as const

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`
  return value
}

function downloadAlertsCsv(headers: readonly string[], rows: string[][], filename: string): void {
  const lines = [
    headers.map(csvEscape).join(','),
    ...rows.map((row) => row.map(csvEscape).join(',')),
  ]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

type AlertRow = {
  id: string
  status: string
  sent_at: string
  reminder_count?: number
  reminded_on_days?: number[] | null
  form_sent_at?: string | null
  last_reminder_at?: string | null
  unique_id?: string | null
  feedback_mode?: string | null
  alert_payload: AlertPayload
  attachment_path?: string | null
  attachment_name?: string | null
  attachment_content_type?: string | null
}

type LineAnswers = {
  impactAreas: string[]
  supplyStatus: string
  impactDuration: string
  impactDetails: string
}

type ReminderDayState = 'sent' | 'pending' | 'missed'

function statusTone(status: string): 'riskHigh' | 'riskMedium' | 'riskLow' | 'quiet' {
  if (status === 'feedback_received') return 'riskLow'
  if (status === 'overdue') return 'riskHigh'
  if (status === 'partial_feedback') return 'riskMedium'
  if (status === 'awaiting_feedback') return 'riskMedium'
  return 'quiet'
}

function calendarDayNumber(sentAt: string, now = new Date()): number {
  const start = new Date(sentAt)
  const ms = now.getTime() - start.getTime()
  return Math.floor(ms / (24 * 60 * 60 * 1000)) + 1
}

function parseRemindOnDays(customizations: unknown): number[] {
  const cfg = (customizations as any)?.supplierRiskAlertFeedback || {}
  const raw = cfg.feedbackForm?.remindOnDays ?? cfg.feedbackForm?.actionDays
  if (Array.isArray(raw) && raw.length) {
    return [...new Set(raw.map((n: unknown) => Number(n)).filter((n: number) => Number.isFinite(n) && n > 0))].sort(
      (a, b) => a - b,
    )
  }
  return [3, 5]
}

function remindedDaysList(row: AlertRow): number[] {
  const raw = row.reminded_on_days
  if (!Array.isArray(raw)) return []
  return raw.map(Number).filter((n) => Number.isFinite(n) && n > 0)
}

function reminderDayState(
  actionDay: number,
  row: AlertRow,
  now = new Date(),
): ReminderDayState | null {
  const sent = remindedDaysList(row)
  if (sent.includes(actionDay)) return 'sent'
  if (row.status === 'feedback_received') return null
  const open =
    row.status === 'awaiting_feedback' ||
    row.status === 'partial_feedback' ||
    row.status === 'overdue'
  if (!open) return null
  const day = calendarDayNumber(row.sent_at, now)
  return day < actionDay ? 'pending' : 'missed'
}

function reminderStateTone(state: ReminderDayState): 'riskHigh' | 'riskMedium' | 'riskLow' | 'quiet' {
  if (state === 'sent') return 'riskLow'
  if (state === 'missed') return 'riskMedium'
  return 'quiet'
}

function reminderStateLabel(state: ReminderDayState): string {
  if (state === 'sent') return 'sent'
  if (state === 'missed') return 'missed'
  return 'due'
}

function emptyLineAnswers(): LineAnswers {
  return {
    impactAreas: [],
    supplyStatus: SUPPLY_STATUSES[0],
    impactDuration: IMPACT_DURATIONS[0],
    impactDetails: '',
  }
}

/** Client inbox: company alerts + tracked impact responses */
export function SupplierRiskAlertsInboxView() {
  const { user, effectiveCompanyId, isAdmin } = useAuth()
  const { company, settings } = useCompany()
  const logoUrl = getCompanyLogoUrl(settings?.theme_config?.logo)
  const remindOnDays = useMemo(
    () => parseRemindOnDays(settings?.customizations),
    [settings?.customizations],
  )

  const [rows, setRows] = useState<AlertRow[]>([])
  const [listProgress, setListProgress] = useState<Record<string, { received: number; total: number }>>({})
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [suppliers, setSuppliers] = useState<AlertSupplierRow[]>([])
  const [feedbackRows, setFeedbackRows] = useState<ImpactFeedback[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [eventAnswers, setEventAnswers] = useState<LineAnswers>(emptyLineAnswers())
  const [lineAnswers, setLineAnswers] = useState<Record<string, LineAnswers>>({})
  const [selectedLineIds, setSelectedLineIds] = useState<Record<string, boolean>>({})
  const [busy, setBusy] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [downloadBusy, setDownloadBusy] = useState(false)
  const [downloadError, setDownloadError] = useState('')
  const [filterSupplier, setFilterSupplier] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [filterBrand, setFilterBrand] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [allLines, setAllLines] = useState<
    { alert_id: string; supplier_name: string; category: string | null; brand: string | null; unique_id: string | null }[]
  >([])

  const companyId = effectiveCompanyId || company?.id || null
  const selected = rows.find((r) => r.id === selectedId) || null
  const feedbackMode =
    selected?.feedback_mode === 'per_supplier' || selected?.alert_payload?.feedbackMode === 'per_supplier'
      ? 'per_supplier'
      : 'event'

  const feedbackByLine = useMemo(() => {
    const map = new Map<string, ImpactFeedback>()
    for (const f of feedbackRows) {
      if (f.supplier_line_id) map.set(f.supplier_line_id, f)
    }
    return map
  }, [feedbackRows])

  const eventFeedback = useMemo(
    () => feedbackRows.find((f) => !f.supplier_line_id) || null,
    [feedbackRows],
  )

  const filterOptions = useMemo(() => {
    const categories = new Set<string>()
    const brands = new Set<string>()
    const statuses = new Set<string>()
    for (const r of rows) {
      statuses.add(r.status)
      const p = r.alert_payload || {}
      for (const s of listSuppliersFromPayload(p)) {
        if (s.category) categories.add(s.category)
        if (s.brand) brands.add(String(s.brand))
      }
    }
    for (const l of allLines) {
      if (l.category) categories.add(l.category)
      if (l.brand) brands.add(l.brand)
    }
    return {
      categories: [...categories].sort(),
      brands: [...brands].sort(),
      statuses: [...statuses].sort(),
    }
  }, [rows, allLines])

  const filteredRows = useMemo(() => {
    const q = filterSupplier.trim().toLowerCase()
    return rows.filter((r) => {
      if (filterStatus && r.status !== filterStatus) return false
      const p = r.alert_payload || {}
      const payloadSuppliers = listSuppliersFromPayload(p)
      const dbLines = allLines.filter((l) => l.alert_id === r.id)
      const names = [
        ...payloadSuppliers.map((s) => s.supplierName),
        ...dbLines.map((l) => l.supplier_name),
        p.supplierName || '',
        alertHeadline(p),
      ]
        .filter(Boolean)
        .map((n) => String(n).toLowerCase())
      if (q && !names.some((n) => n.includes(q))) return false

      const cats = [
        ...payloadSuppliers.map((s) => s.category).filter(Boolean),
        ...dbLines.map((l) => l.category).filter(Boolean),
        p.category,
      ].map((c) => String(c))
      if (filterCategory && !cats.includes(filterCategory)) return false

      const brands = [
        ...payloadSuppliers.map((s) => s.brand).filter(Boolean),
        ...dbLines.map((l) => l.brand).filter(Boolean),
        p.brand,
      ].map((b) => String(b))
      if (filterBrand && !brands.includes(filterBrand)) return false

      return true
    })
  }, [rows, allLines, filterSupplier, filterCategory, filterBrand, filterStatus])

  useEffect(() => {
    if (!selectedId) return
    if (!filteredRows.some((r) => r.id === selectedId)) {
      setSelectedId(filteredRows[0]?.id || null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows])

  function exportFilteredCsv() {
    const reminderHeaders = remindOnDays.map((d) => `day_${d}_reminder`)
    const headers = [...CSV_BASE_HEADERS, ...reminderHeaders]
    const out: string[][] = []
    for (const r of filteredRows) {
      const p = r.alert_payload || {}
      const title = alertHeadline(p)
      const mode = r.feedback_mode || p.feedbackMode || 'event'
      const prog = listProgress[r.id]
      const progress = prog ? `${prog.received}/${prog.total}` : ''
      const reminderFlags = remindOnDays.map((d) => {
        const state = reminderDayState(d, r)
        return state || (remindedDaysList(r).includes(d) ? 'sent' : '')
      })
      const dbLines = allLines.filter((l) => l.alert_id === r.id)
      const payloadSuppliers = listSuppliersFromPayload(p)
      const lineSources =
        dbLines.length > 0
          ? dbLines.map((l) => ({
              name: l.supplier_name,
              category: l.category || '',
              brand: l.brand || '',
              uniqueId: l.unique_id || '',
            }))
          : payloadSuppliers.map((s) => ({
              name: s.supplierName,
              category: s.category || p.category || '',
              brand: s.brand ? String(s.brand) : p.brand || '',
              uniqueId: s.uniqueId || p.uniqueId || '',
            }))
      if (!lineSources.length) {
        out.push([
          r.sent_at,
          r.status,
          title,
          p.supplierName || '',
          p.category || '',
          p.brand || '',
          r.unique_id || p.uniqueId || '',
          String(mode),
          progress,
          ...reminderFlags,
        ])
      } else {
        for (const s of lineSources) {
          out.push([
            r.sent_at,
            r.status,
            title,
            s.name,
            s.category,
            s.brand,
            s.uniqueId,
            String(mode),
            progress,
            ...reminderFlags,
          ])
        }
      }
    }
    downloadAlertsCsv(
      headers,
      out,
      `risk-alerts-${new Date().toISOString().slice(0, 10)}.csv`,
    )
  }

  const receivedCount = suppliers.filter((s) => s.line_status === 'received').length
  const pendingSuppliers = suppliers.filter((s) => s.line_status !== 'received')

  async function loadList() {
    if (!companyId) {
      setRows([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      const { data, error: qErr } = await supabase
        .from('supplier_risk_alerts')
        .select(
          'id, status, sent_at, reminder_count, reminded_on_days, form_sent_at, last_reminder_at, unique_id, feedback_mode, alert_payload, attachment_path, attachment_name, attachment_content_type',
        )
        .eq('company_id', companyId)
        .order('sent_at', { ascending: false })
        .limit(100)
      if (qErr) throw qErr
      const alertRows = (data as AlertRow[]) || []
      setRows(alertRows)
      if (!selectedId && alertRows.length) setSelectedId(alertRows[0].id)
      if (alertRows.length) {
        const ids = alertRows.map((r) => r.id)
        const { data: lineRows } = await supabase
          .from('supplier_risk_alert_suppliers')
          .select('alert_id, line_status, supplier_name, category, brand, unique_id')
          .in('alert_id', ids)
        const prog: Record<string, { received: number; total: number }> = {}
        const linesOut: {
          alert_id: string
          supplier_name: string
          category: string | null
          brand: string | null
          unique_id: string | null
        }[] = []
        for (const line of lineRows || []) {
          const id = (line as any).alert_id as string
          if (!prog[id]) prog[id] = { received: 0, total: 0 }
          prog[id].total += 1
          if ((line as any).line_status === 'received') prog[id].received += 1
          linesOut.push({
            alert_id: id,
            supplier_name: (line as any).supplier_name,
            category: (line as any).category ?? null,
            brand: (line as any).brand ?? null,
            unique_id: (line as any).unique_id ?? null,
          })
        }
        setListProgress(prog)
        setAllLines(linesOut)
      } else {
        setListProgress({})
        setAllLines([])
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load alerts')
    } finally {
      setLoading(false)
    }
  }

  async function loadDetail(alertId: string) {
    const [{ data: lines }, { data: fbs }] = await Promise.all([
      supabase
        .from('supplier_risk_alert_suppliers')
        .select(
          'id, sort_order, supplier_name, unique_id, category, sub_category, plant, brand, location, region, single_source, line_status',
        )
        .eq('alert_id', alertId)
        .order('sort_order', { ascending: true }),
      supabase
        .from('supplier_risk_impact_feedback')
        .select(
          'impact_area, impact_areas, supply_status, impact_duration, impact_details, submitted_by_email, submitted_at, supplier_line_id',
        )
        .eq('alert_id', alertId),
    ])
    const supplierRows = (lines as AlertSupplierRow[]) || []
    setSuppliers(supplierRows)
    setFeedbackRows((fbs as ImpactFeedback[]) || [])
    setSubmitError('')
    setDownloadError('')
    setEventAnswers(emptyLineAnswers())
    const nextAnswers: Record<string, LineAnswers> = {}
    const nextSelected: Record<string, boolean> = {}
    for (const s of supplierRows) {
      if (s.line_status !== 'received') {
        nextAnswers[s.id] = emptyLineAnswers()
        nextSelected[s.id] = true
      }
    }
    setLineAnswers(nextAnswers)
    setSelectedLineIds(nextSelected)
  }

  async function downloadAttachment(row: AlertRow) {
    if (!row.attachment_path) return
    setDownloadBusy(true)
    setDownloadError('')
    try {
      const { data, error: urlErr } = await supabase.storage
        .from(SUPPLIER_RISK_ALERT_DOC_UPLOAD_CONFIG.STORAGE_BUCKET)
        .createSignedUrl(row.attachment_path, SIGNED_URL_EXPIRY_SEC)
      if (urlErr || !data?.signedUrl) throw urlErr || new Error('Could not create download link')
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
    } catch (e: any) {
      setDownloadError(e.message || 'Download failed')
    } finally {
      setDownloadBusy(false)
    }
  }

  useEffect(() => {
    loadList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  useEffect(() => {
    if (!selectedId) {
      setSuppliers([])
      setFeedbackRows([])
      return
    }
    loadDetail(selectedId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId])

  async function onSubmitEvent(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedId) return
    if (!eventAnswers.impactAreas.length) {
      setSubmitError('Select at least one Area of Potential Impact.')
      return
    }
    setBusy(true)
    setSubmitError('')
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('submit-impact-feedback', {
        body: {
          alertId: selectedId,
          impactAreas: eventAnswers.impactAreas,
          supplyStatus: eventAnswers.supplyStatus,
          impactDuration: eventAnswers.impactDuration,
          impactDetails: eventAnswers.impactDetails,
          submittedByEmail: user?.email || '',
        },
      })
      if (fnErr) throw fnErr
      if (data?.error) throw new Error(data.error)
      await loadList()
      await loadDetail(selectedId)
    } catch (err: any) {
      setSubmitError(err.message || 'Submit failed')
    } finally {
      setBusy(false)
    }
  }

  async function onSubmitPerSupplier(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedId) return
    setBusy(true)
    setSubmitError('')
    try {
      const responses = pendingSuppliers
        .filter((s) => selectedLineIds[s.id])
        .map((s) => {
          const a = lineAnswers[s.id]
          return {
            supplierLineId: s.id,
            impactAreas: a?.impactAreas || [],
            supplyStatus: a?.supplyStatus,
            impactDuration: a?.impactDuration,
            impactDetails: a?.impactDetails,
          }
        })
        .filter((r) => String(r.impactDetails || '').trim() && (r.impactAreas?.length || 0) > 0)
      if (!responses.length) {
        throw new Error(
          'Fill in at least one pending supplier (including Area(s) of Potential Impact).',
        )
      }

      const { data, error: fnErr } = await supabase.functions.invoke('submit-impact-feedback', {
        body: {
          alertId: selectedId,
          responses,
          submittedByEmail: user?.email || '',
        },
      })
      if (fnErr) throw fnErr
      if (data?.error) throw new Error(data.error)
      await loadList()
      await loadDetail(selectedId)
    } catch (err: any) {
      setSubmitError(err.message || 'Submit failed')
    } finally {
      setBusy(false)
    }
  }

  if (!companyId && !isAdmin) {
    return <p className="text-sm text-es-muted">No company context. Sign in with a company account.</p>
  }

  if (!companyId && isAdmin) {
    return (
      <p className="text-sm text-es-muted">
        Select <strong>View as</strong> a company to see that client’s Risk alerts inbox, or open Send alert to
        compose.
      </p>
    )
  }

  const canSubmit =
    selected &&
    selected.status !== 'feedback_received' &&
    (selected.status === 'awaiting_feedback' ||
      selected.status === 'partial_feedback' ||
      selected.status === 'overdue')

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {logoUrl && <img src={logoUrl} alt="" className="h-9 max-w-[120px] object-contain" />}
        <div>
          <h1 className="font-esDisplay text-2xl text-es-navy">Risk alerts</h1>
          <p className="text-sm text-es-muted">
            Alerts sent to <strong>{company?.name || 'your company'}</strong>. Open one to read the alert and your
            tracked response.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm">{error}</div>
      )}
      {loading && <p className="text-sm text-es-muted">Loading…</p>}

      {!loading && rows.length === 0 && (
        <p className="text-sm text-es-muted rounded-lg border border-es-hairline bg-es-white p-5">
          No supplier risk alerts yet for this company.
        </p>
      )}

      {rows.length > 0 && (
        <>
          <div className="rounded-lg border border-es-hairline bg-es-white p-4 flex flex-wrap gap-3 items-end">
            <label className="text-sm min-w-[140px] flex-1">
              <span className="text-xs text-es-muted">Supplier</span>
              <input
                className={`${field} mt-1`}
                value={filterSupplier}
                onChange={(e) => setFilterSupplier(e.target.value)}
                placeholder="Search name…"
              />
            </label>
            <label className="text-sm min-w-[120px]">
              <span className="text-xs text-es-muted">Category</span>
              <select
                className={`${field} mt-1`}
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">All</option>
                {filterOptions.categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm min-w-[120px]">
              <span className="text-xs text-es-muted">Brand</span>
              <select
                className={`${field} mt-1`}
                value={filterBrand}
                onChange={(e) => setFilterBrand(e.target.value)}
              >
                <option value="">All</option>
                {filterOptions.brands.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm min-w-[120px]">
              <span className="text-xs text-es-muted">Status</span>
              <select
                className={`${field} mt-1`}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All</option>
                {filterOptions.statuses.map((s) => (
                  <option key={s} value={s}>
                    {s.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={exportFilteredCsv}
              className="rounded-full border border-es-hairline bg-es-haze px-4 py-2 text-sm font-semibold text-es-navy hover:bg-es-white"
            >
              Download CSV ({filteredRows.length})
            </button>
          </div>

        <div className="grid lg:grid-cols-[320px_1fr] gap-4">
          <ul className="rounded-lg border border-es-hairline bg-es-white divide-y divide-es-hairline max-h-[70vh] overflow-y-auto">
            {filteredRows.length === 0 && (
              <li className="px-4 py-6 text-sm text-es-muted">No alerts match these filters.</li>
            )}
            {filteredRows.map((r) => {
              const p = r.alert_payload || {}
              const fromPayload = listSuppliersFromPayload(p)
              const prog = listProgress[r.id]
              const n = prog?.total || fromPayload.length || 1
              const title = alertHeadline(p) || r.unique_id || 'Alert'
              const mode = r.feedback_mode === 'per_supplier' ? 'per_supplier' : 'event'
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-es-haze ${
                      selectedId === r.id ? 'bg-es-haze' : ''
                    }`}
                  >
                    <p className="text-sm font-semibold text-es-navy truncate">{title}</p>
                    <p className="text-xs text-es-muted mt-0.5">
                      {new Date(r.sent_at).toLocaleString()} · {String(p.severity || '').toUpperCase() || '—'}
                      {n > 1 ? ` · ${n} suppliers` : ''}
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      <EsBadge tone={statusTone(r.status)}>{r.status.replace(/_/g, ' ')}</EsBadge>
                      {mode === 'per_supplier' && prog && prog.total > 0 && (
                        <EsBadge tone="quiet">
                          {prog.received}/{prog.total} answered
                        </EsBadge>
                      )}
                      {remindOnDays.map((d) => {
                        const state = reminderDayState(d, r)
                        if (!state) return null
                        return (
                          <EsBadge key={`d${d}`} tone={reminderStateTone(state)}>
                            Day {d} · {reminderStateLabel(state)}
                          </EsBadge>
                        )
                      })}
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>

          <div className="space-y-4 min-w-0">
            {selected && (
              <>
                <SupplierRiskAlertDocument
                  payload={selected.alert_payload || {}}
                  logoUrl={logoUrl}
                  status={selected.status}
                />

                <div className="rounded-lg border border-es-hairline bg-es-white px-4 py-3">
                  <p className="text-sm font-medium text-es-navy">Reminder emails</p>
                  <p className="text-xs text-es-muted mt-0.5">
                    Day 1 is the initial alert. Day {remindOnDays.join(' / ')} are impact-feedback reminders.
                  </p>
                  <ul className="mt-3 space-y-2">
                    <li className="flex flex-wrap items-center gap-2 text-sm text-es-navy">
                      <span className="font-medium w-14">Day 1</span>
                      <EsBadge tone="riskLow">sent</EsBadge>
                      <span className="text-xs text-es-muted">
                        {new Date(selected.sent_at).toLocaleString()}
                      </span>
                    </li>
                    {remindOnDays.map((d) => {
                      const state = reminderDayState(d, selected)
                      const shown = state || (remindedDaysList(selected).includes(d) ? 'sent' : null)
                      if (!shown) {
                        return (
                          <li key={d} className="flex flex-wrap items-center gap-2 text-sm text-es-navy">
                            <span className="font-medium w-14">Day {d}</span>
                            <EsBadge tone="quiet">not sent</EsBadge>
                          </li>
                        )
                      }
                      return (
                        <li key={d} className="flex flex-wrap items-center gap-2 text-sm text-es-navy">
                          <span className="font-medium w-14">Day {d}</span>
                          <EsBadge tone={reminderStateTone(shown)}>{reminderStateLabel(shown)}</EsBadge>
                          {shown === 'sent' && selected.last_reminder_at && remindedDaysList(selected).length === 1 && (
                            <span className="text-xs text-es-muted">
                              {new Date(selected.last_reminder_at).toLocaleString()}
                            </span>
                          )}
                          {shown === 'sent' &&
                            selected.last_reminder_at &&
                            remindedDaysList(selected).length > 1 &&
                            d === Math.max(...remindedDaysList(selected)) && (
                              <span className="text-xs text-es-muted">
                                last {new Date(selected.last_reminder_at).toLocaleString()}
                              </span>
                            )}
                        </li>
                      )
                    })}
                  </ul>
                </div>

                {selected.attachment_path && (
                  <div className="rounded-lg border border-es-hairline bg-es-white px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-es-navy">Original alert document</p>
                      <p className="text-xs text-es-muted truncate">
                        {selected.attachment_name || selected.attachment_path.split('/').pop()}
                      </p>
                      {downloadError && (
                        <p className="text-xs text-red-700 mt-1">{downloadError}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={downloadBusy}
                      onClick={() => downloadAttachment(selected)}
                      className="rounded-full border border-es-hairline bg-es-white px-4 py-2 text-sm font-semibold text-es-navy hover:bg-es-haze disabled:opacity-50"
                    >
                      {downloadBusy ? 'Preparing…' : 'Download original'}
                    </button>
                  </div>
                )}

                {feedbackMode === 'event' && eventFeedback && (
                  <ImpactFeedbackPanel feedback={eventFeedback} />
                )}

                {feedbackMode === 'per_supplier' && (
                  <div className="space-y-3">
                    <p className="text-sm text-es-muted">
                      Impact progress: <strong>{receivedCount}</strong> of <strong>{suppliers.length || 0}</strong>{' '}
                      suppliers
                    </p>
                    {suppliers.map((s) => {
                      const fb = feedbackByLine.get(s.id)
                      if (s.line_status === 'received' && fb) {
                        return (
                          <ImpactFeedbackPanel
                            key={s.id}
                            title={`${s.supplier_name} — response`}
                            feedback={fb}
                          />
                        )
                      }
                      if (s.line_status === 'received') {
                        return (
                          <div
                            key={s.id}
                            className="rounded-lg border border-es-hairline bg-es-white px-4 py-3 text-sm"
                          >
                            <p className="font-medium text-es-navy">{s.supplier_name}</p>
                            <p className="text-xs text-es-muted mt-0.5">Response received</p>
                          </div>
                        )
                      }
                      return (
                        <div
                          key={s.id}
                          className="rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-3 text-sm"
                        >
                          <p className="font-medium text-es-navy">{s.supplier_name}</p>
                          <p className="text-xs text-amber-900 mt-0.5">Pending assessment</p>
                        </div>
                      )
                    })}
                  </div>
                )}

                {canSubmit && feedbackMode === 'event' && !eventFeedback && (
                  <form
                    onSubmit={onSubmitEvent}
                    className="rounded-lg border border-es-hairline bg-es-white p-5 space-y-4"
                  >
                    <h3 className="font-esDisplay text-lg text-es-navy">Submit update</h3>
                    <p className="text-sm text-es-muted">
                      Provide one impact assessment for this event. Status updates automatically when you submit.
                    </p>
                    {submitError && (
                      <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-3 py-2 text-sm">
                        {submitError}
                      </div>
                    )}
                    <InboxQuestionFields
                      answers={eventAnswers}
                      onChange={(patch) => setEventAnswers((prev) => ({ ...prev, ...patch }))}
                      namePrefix="event"
                    />
                    <button
                      type="submit"
                      disabled={busy}
                      className="rounded-full bg-es-signal text-es-white px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
                    >
                      {busy ? 'Submitting…' : 'Submit update'}
                    </button>
                  </form>
                )}

                {canSubmit && feedbackMode === 'per_supplier' && pendingSuppliers.length > 0 && (
                  <form
                    onSubmit={onSubmitPerSupplier}
                    className="rounded-lg border border-es-hairline bg-es-white p-5 space-y-4"
                  >
                    <h3 className="font-esDisplay text-lg text-es-navy">Submit supplier updates</h3>
                    <p className="text-sm text-es-muted">
                      Answer for one or more pending suppliers. You can come back for the rest.
                    </p>
                    {submitError && (
                      <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-3 py-2 text-sm">
                        {submitError}
                      </div>
                    )}
                    {pendingSuppliers.map((s) => (
                      <div key={s.id} className="rounded-lg border border-es-hairline p-4 space-y-3">
                        <label className="flex items-center gap-2 text-sm font-medium">
                          <input
                            type="checkbox"
                            checked={Boolean(selectedLineIds[s.id])}
                            onChange={(e) =>
                              setSelectedLineIds((prev) => ({ ...prev, [s.id]: e.target.checked }))
                            }
                          />
                          {s.supplier_name}
                        </label>
                        {selectedLineIds[s.id] && lineAnswers[s.id] && (
                          <InboxQuestionFields
                            answers={lineAnswers[s.id]}
                            onChange={(patch) =>
                              setLineAnswers((prev) => ({
                                ...prev,
                                [s.id]: { ...prev[s.id], ...patch },
                              }))
                            }
                            namePrefix={s.id}
                          />
                        )}
                      </div>
                    ))}
                    <button
                      type="submit"
                      disabled={busy}
                      className="rounded-full bg-es-signal text-es-white px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
                    >
                      {busy ? 'Submitting…' : 'Submit selected'}
                    </button>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  )
}

function InboxQuestionFields({
  answers,
  onChange,
  namePrefix,
}: {
  answers: LineAnswers
  onChange: (patch: Partial<LineAnswers>) => void
  namePrefix: string
}) {
  return (
    <>
      <fieldset>
        <legend className="text-sm font-medium">Area(s) of Potential Impact</legend>
        <p className="mt-1 text-xs text-es-muted">Select all that apply</p>
        <div className="mt-2 space-y-2">
          {IMPACT_AREAS.map((o) => (
            <label key={o} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                name={`${namePrefix}-impactAreas`}
                checked={answers.impactAreas.includes(o)}
                onChange={() => onChange({ impactAreas: toggleImpactArea(answers.impactAreas, o) })}
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend className="text-sm font-medium">Supply / service status</legend>
        <div className="mt-2 space-y-2">
          {SUPPLY_STATUSES.map((o) => (
            <label key={o} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name={`${namePrefix}-supplyStatus`}
                checked={answers.supplyStatus === o}
                onChange={() => onChange({ supplyStatus: o })}
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend className="text-sm font-medium">Expected impact duration</legend>
        <div className="mt-2 space-y-2">
          {IMPACT_DURATIONS.map((o) => (
            <label key={o} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name={`${namePrefix}-impactDuration`}
                checked={answers.impactDuration === o}
                onChange={() => onChange({ impactDuration: o })}
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <label className="block text-sm">
        <span className="font-medium">Impact details</span>
        <textarea
          className={`${field} mt-1`}
          required
          maxLength={250}
          rows={4}
          value={answers.impactDetails}
          onChange={(e) => onChange({ impactDetails: e.target.value })}
        />
        <span className="text-xs text-es-muted">{answers.impactDetails.length}/250</span>
      </label>
    </>
  )
}
